import { useIdentifiedScalesResults } from '@mentimeter/presentation-data-hooks/results/identified/scales';
import type { ScalesT } from '@mentimeter/question-modules-types';
import type { ScalesStaticContent } from '@core-api/presentation/types/response';
import { Box } from '@mentimeter/ragnar-ui/box';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { ScalesPresentation } from './ScalesPresentation';
import { TrendsDataHandler } from './visualizations/Trends';
import {
  SCALES_FALLBACK_MAX_RANGE,
  SCALES_FALLBACK_MIN_RANGE,
} from './utils/constants';

export function ScalesPresentationDataHandler() {
  const {
    useQuestion,
    useResults,
    useTrends,
    useSeries,
    useCurrentSession,
    useConfig,
    usePace,
    InteractiveCanvasComponents,
  } = usePresentationContext();

  const slide = useQuestion();

  const { currentStep } = usePace();

  const { showTrends: shouldShowTrends } = useTrends();
  const { results, respondents } = useResults<ScalesT>();
  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const { isCurrentSessionActive } = useCurrentSession();
  const { enableAnimations, mode } = useConfig();

  const staticContent = slide.staticContent as ScalesStaticContent;

  const isSubTypeWithIdentifiedResponses =
    staticContent.interactiveContentStyling.visualization === 'slider-chart';

  const hasIdentifiedResultsSettingOn =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldFetchIdentifiedResults =
    hasIdentifiedResultsSettingOn &&
    isSubTypeWithIdentifiedResponses &&
    isCurrentSessionActive;

  const { data: identifiedResults } = useIdentifiedScalesResults(
    slide.slideAdminKey,
    shouldFetchIdentifiedResults,
  );

  const choices = slide.interactiveContents[0]!.choices.map(
    (choice, index) => ({
      // using legacyChoiceId for now because results use it
      // fallback is the same kind we do in the compatibility layer, it can happen when creating a new slide or adding a choice (TODO: fix that in the future)
      id: choice.legacyChoiceId ?? index + 1,
      label: choice.title,
    }),
  );

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const range = {
    min:
      slide.interactiveContents[0]!.responseRange?.min ??
      SCALES_FALLBACK_MIN_RANGE,
    max:
      slide.interactiveContents[0]!.responseRange?.max ??
      SCALES_FALLBACK_MAX_RANGE,
  };

  return (
    <ScalesPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={slide.interactiveContents[0]!.description}
      fontSizeOffset={staticContent.textSizeOffset}
      identifiedResults={identifiedResults?.results}
      choices={choices}
      range={range}
      visualizationType={staticContent.interactiveContentStyling.visualization}
      numberOfRespondents={respondents}
      results={results}
      shouldHideResults={
        slide.responseVisibility === 'hidden' ||
        visualizationState === 'hide-answers'
      }
      dimensionLabels={{
        min: staticContent.interactiveContentStyling.responseRangeLabels
          .minimumValueLabel,
        max: staticContent.interactiveContentStyling.responseRangeLabels
          .maximumValueLabel,
      }}
      shouldShowOverallAverage={
        staticContent.interactiveContentStyling.showAverage
      }
      shouldAnimate={enableAnimations}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
    >
      {shouldShowTrends && (
        <Box flex="1" width="100%">
          <TrendsDataHandler
            choices={choices}
            range={range}
            shouldHideResults={
              slide.responseVisibility === 'hidden' ||
              visualizationState === 'hide-answers'
            }
          />
        </Box>
      )}
    </ScalesPresentation>
  );
}
